import type { UnitsSettings } from "../../../app/units/units";
import { spacing } from "../../../app/design-system";
import { Alert,Flex,Form,InputNumber,Select,Typography } from "../../../../../../../shared/frontend/ui";
import { DetailField, DetailList } from "../../../../../../../shared/frontend/components/DetailList";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React from "react";
import { ACCESS_SCOPE_STUDIO_WHITELIST } from "../../../app/constants/accessScope";
import { PROPORTIONAL_METHOD_FREE } from "../../../app/constants/proportionalCalculationMethod";
import { calculateProportionalPreview,getWalletType,round2AwayFromZero,shouldShowProportionalFeeBaseBlock } from "../../../app/utils/contractCreation";
import { formatDateOnly,formatMoney,formatWalletBalance } from "../../../app/units/format";
import { ContractOptionGroupSelector } from "../../../components/ContractOptionGroupSelector";
import type { ContractOptionGroupDto, OptionGroupSelectionState } from "../../../app/utils/contractOptionGroups";
import { ResponsiveFormGrid } from "../../../components/ui/antd";
import { FormSectionHeading } from "../../../components/FormSectionHeading";
import { RepeatableBlock } from "../../../components/RepeatableBlocks";
import "./personOverview.css";
import type {
  ContractServiceDraft,
  ContractTemplateComponentDto,
  ContractTemplateDto,
  ContractWalletDraft,
  WalletProductDto
} from "./personDetailTypes";
import { formatDateOnlyIso } from "./personDetailUtils";
import type { TimeUnitValue } from "../../../app/constants/timeUnit";

const BLOCK_DETAIL_PAYMENT_CYCLE = "paymentCycle";
const BLOCK_DETAIL_ACCESS = "access";

type SelectOption = {
  value: string | number;
  label: React.ReactNode;
};

type StudioOption = {
  id: string;
  name: string;
};

type InstallmentPlanMeta = {
  planId: string;
  planName: string;
  min: number;
  max: number;
};

type InstallmentPreview = {
  count: number;
  installment: number;
  total: number;
  cycle: string;
};

type ContractBlockDetail = {
  key: string;
  label: string;
  value: string;
};

type PersonContractCreateContentsSectionProps = {
  selectedVariant: ContractTemplateDto | null;
  selectedVariantMeta: { allowOverridesOnCreate: boolean } | null;
  bundleServiceDraft: ContractServiceDraft[];
  setBundleServiceDraft: React.Dispatch<React.SetStateAction<ContractServiceDraft[]>>;
  bundleWalletDraft: ContractWalletDraft[];
  contractOptionGroups: ContractOptionGroupDto[];
  optionGroupSelection: OptionGroupSelectionState;
  setOptionGroupSelection: (next: OptionGroupSelectionState) => void;
  contractTemplateDefaultPriceOf: (contractTemplateId: string) => number | null | undefined;
  watchedContractStartDate?: Dayjs | null;
  watchedTrainingStartDate?: Dayjs | null;
  primaryService: ContractServiceDraft | ContractTemplateComponentDto | null;
  installmentPlanMeta: InstallmentPlanMeta | null;
  selectedPaymentPlan: unknown | null;
  installmentPreview: InstallmentPreview | null;
  walletProducts: WalletProductDto[];
  allowedStudios: StudioOption[];
  paymentMethodSelectOptions: SelectOption[];
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  units: UnitsSettings;
  text: (key: string) => string;
  billingCycleLabel: (value?: number | null, unit?: number | null) => string;
  cycleUnitLabel: (unit?: number | null) => string;
  usageLimitUnitLabel: (unit?: TimeUnitValue | null) => string;
  timeUnitLabel: (unit?: number | null) => string;
  accessScopeLabel: (scope?: number | null) => string;
};

export function PersonContractCreateContentsSection({
  selectedVariant,
  selectedVariantMeta,
  bundleServiceDraft,
  setBundleServiceDraft,
  bundleWalletDraft,
  contractOptionGroups,
  optionGroupSelection,
  setOptionGroupSelection,
  contractTemplateDefaultPriceOf,
  watchedContractStartDate,
  watchedTrainingStartDate,
  primaryService,
  installmentPlanMeta,
  selectedPaymentPlan,
  installmentPreview,
  walletProducts,
  allowedStudios,
  paymentMethodSelectOptions,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  units,
  text,
  billingCycleLabel,
  cycleUnitLabel,
  usageLimitUnitLabel,
  timeUnitLabel,
  accessScopeLabel
}: PersonContractCreateContentsSectionProps) {
  // Je Baustein und Wertekonto eine Kit-Karte (RepeatableBlock) mit Preisfeld und Kenndaten
  // nebeneinander (DetailList wie in der Personenübersicht) — kein eigener Rahmen, keine
  // handgebauten Label-Wert-Zeilen.
  const details = (items: ContractBlockDetail[]) => items.length ? (
    <DetailList className="person-overview-inline-details">
      {items.map((d) => <DetailField key={d.key} label={d.label}>{d.value}</DetailField>)}
    </DetailList>
  ) : null;

  const moneyInput = (value: number | null | undefined, disabled: boolean, onChange: (v: number | null) => void) => (
    <InputNumber
      disabled={disabled}
      value={value ?? undefined}
      style={{ width: "100%" }}
      min={0}
      precision={2}
      step={0.01}
      decimalSeparator={decimalSeparator}
      placeholder={moneyPlaceholder}
      addonAfter={currencyCode}
      onChange={(v) => onChange(v == null ? null : Number(v))}
    />
  );

  return (
    <>
      <FormSectionHeading title={text("Contract blocks")} sectionKey="person-contract-create-contents.contract-blocks">
        {!selectedVariant ? (
          <Alert type="info" title={text("Please select a contract package first to view included items.")} />
        ) : (
          <>
            <Flex vertical gap={spacing.md}>
              {selectedVariantMeta?.allowOverridesOnCreate === false ? (
                <Alert
                  type="warning"
                  title={text("This template does not allow overrides on creation — items are taken from the package and shown here for review only.")}
                />
              ) : null}

              {(bundleServiceDraft || []).length === 0 ? (
                <Typography.Text type="secondary">{text("No contract blocks in this package.")}</Typography.Text>
              ) : (
                (bundleServiceDraft || []).map((r, idx) => {
                  const contractStartIso = formatDateOnlyIso(watchedContractStartDate);
                  const trainingIso = formatDateOnlyIso(watchedTrainingStartDate);
                  if (!shouldShowProportionalFeeBaseBlock(r, trainingIso, contractStartIso)) {
                    return null;
                  }
                  const allowOverrides = selectedVariantMeta?.allowOverridesOnCreate !== false;
                  const priceEditable = r.allowPriceOverrideOnCreate !== false;
                  const blockDetails: ContractBlockDetail[] = [];
                  const payCycle = billingCycleLabel(r.paymentCycleValue, r.paymentCycleUnit);
                  if (payCycle) blockDetails.push({ key: BLOCK_DETAIL_PAYMENT_CYCLE, label: text("Payment cycle"), value: payCycle });
                  if (r.durationValue != null && r.durationUnit != null) {
                    const u = cycleUnitLabel(r.durationUnit);
                    if (u) blockDetails.push({ key: "duration", label: text("Duration"), value: `${Number(r.durationValue)} ${u}` });
                  }
                  if (r.renewalValue != null && r.renewalUnit != null) {
                    const u = cycleUnitLabel(r.renewalUnit);
                    if (u) blockDetails.push({ key: "renewal", label: text("Renewal"), value: `${Number(r.renewalValue)} ${u}` });
                  }
                  if (r.usageLimitValue != null && r.usageLimitUnit != null) {
                    const u = usageLimitUnitLabel(r.usageLimitUnit);
                    if (u) blockDetails.push({ key: "usageLimit", label: text("Usage limit"), value: `${Number(r.usageLimitValue)} ${u}` });
                  }
                  if (r.validValue != null && r.validUnit != null) {
                    const u = timeUnitLabel(r.validUnit);
                    if (u) blockDetails.push({ key: "validity", label: text("Validity"), value: `${Number(r.validValue)} ${u}` });
                  }
                  const allowsAccess = r.allowsAccess !== false;
                  const hasAccessScope = Number(r.accessScope ?? 0) > 0;
                  if (allowsAccess && hasAccessScope) {
                    const access = accessScopeLabel(r.accessScope);
                    if (access) blockDetails.push({ key: BLOCK_DETAIL_ACCESS, label: text("Access"), value: access });
                    if (Number(r.accessScope ?? 0) === ACCESS_SCOPE_STUDIO_WHITELIST && (r.allowedStudioIds || []).length) {
                      const names = (r.allowedStudioIds || []).map((id) => allowedStudios.find((s) => String(s.id) === String(id))?.name || String(id));
                      blockDetails.push({ key: "allowedStudios", label: text("Allowed studios"), value: names.join(", ") });
                    }
                  }
                  const isPrimaryBlock =
                    Boolean(r.isPrimary) || (
                      primaryService
                        ? (String(primaryService.serviceProductId || primaryService.contractTemplateId || "") === String(r.serviceProductId || r.contractTemplateId || ""))
                        : false
                    );
                  const activeInstallmentPlanMeta = installmentPlanMeta && selectedPaymentPlan && isPrimaryBlock ? installmentPlanMeta : null;
                  const usesInstallmentPlan = Boolean(activeInstallmentPlanMeta);
                  const visibleDetails = usesInstallmentPlan ? blockDetails.filter((d) => d.key !== BLOCK_DETAIL_PAYMENT_CYCLE) : blockDetails;
                  const paymentMethodField = (
                    <Form.Item name={["servicePaymentMethod", r.serviceProductId]} hidden>
                      <Select options={paymentMethodSelectOptions} />
                    </Form.Item>
                  );

                  // Fest enthaltene Bausteine sind nicht einzeln entfernbar. Wahlfreiheit entsteht über die
                  // Baustein-Gruppen unten — dieselbe Auswahl, die auch das Abschlussformular anbietet und
                  // die der Server gegen die Paketvorlage prüft.
                  if (r.isProportionalFeeBase === true) {
                    let days = 0;
                    if (trainingIso && contractStartIso) {
                      const a = dayjs(trainingIso).startOf("day");
                      const b = dayjs(contractStartIso).startOf("day");
                      if (a.isValid() && b.isValid()) days = Math.max(0, b.diff(a, "day"));
                    }
                    if (days <= 0) return null;

                    const methodRaw = Number(r.proportionalCalculationMethod || 0);
                    const method = methodRaw > 0 ? methodRaw : PROPORTIONAL_METHOD_FREE;
                    const isFreeMethod = method === PROPORTIONAL_METHOD_FREE;

                    let proratedValue: number | null = null;
                    let rangeLabel = "";
                    if (trainingIso && contractStartIso) {
                      rangeLabel = `${formatDateOnly(trainingIso, units)} → ${formatDateOnly(contractStartIso, units)} (${days} ${text("days")})`;
                      const primaryRow = (bundleServiceDraft || []).find((x) => x.isPrimary);
                      const baseValue = primaryRow?.value != null ? Number(primaryRow.value) : 0;
                      if (baseValue > 0) {
                        proratedValue = calculateProportionalPreview(baseValue, trainingIso, contractStartIso, method, primaryRow?.paymentCycleUnit).prorated;
                      } else if (isFreeMethod) {
                        proratedValue = 0;
                      }
                    }

                    const displayValue = r.proportionalValueOverride != null ? Number(r.proportionalValueOverride) : proratedValue;
                    const canEditProportional = allowOverrides && r.allowProportionalValueOverrideOnCreate !== false;
                    const proportionalDetails: ContractBlockDetail[] = [];
                    if (isFreeMethod) proportionalDetails.push({ key: "price", label: text("Price"), value: text("Free") });
                    const payCycleDetail = blockDetails.find((d) => d.key === BLOCK_DETAIL_PAYMENT_CYCLE);
                    if (!isFreeMethod && payCycleDetail) proportionalDetails.push(payCycleDetail);
                    proportionalDetails.push({ key: "period", label: text("Period"), value: rangeLabel });
                    const accessDetail = blockDetails.find((d) => d.key === BLOCK_DETAIL_ACCESS);
                    if (accessDetail) proportionalDetails.push(accessDetail);

                    return (
                      <RepeatableBlock key={`${r.serviceProductId}-${idx}`} title={r.serviceProductName || text("Contract block")} removeLabel={text("Remove")}>
                        {!isFreeMethod ? (
                          <Form.Item label={text("Price")} extra={!canEditProportional ? text("Price is fixed in contract package.") : undefined}>
                            {moneyInput(displayValue ?? 0, !canEditProportional, (v) => {
                              setBundleServiceDraft((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, proportionalValueOverride: v == null ? null : round2AwayFromZero(v) } : x))
                              );
                            })}
                          </Form.Item>
                        ) : null}
                        {paymentMethodField}
                        {details(proportionalDetails)}
                      </RepeatableBlock>
                    );
                  }

                  return (
                    <RepeatableBlock key={`${r.serviceProductId}-${idx}`} title={r.serviceProductName || text("Contract block")} removeLabel={text("Remove")}>
                      <Form.Item
                        label={usesInstallmentPlan ? text("Base price") : payCycle ? `${text("Price")} (${payCycle})` : text("Price")}
                        extra={!priceEditable ? text("Price is fixed in the contract package.") : undefined}
                      >
                        {moneyInput(r.value, !allowOverrides || !priceEditable, (v) => {
                          setBundleServiceDraft((prev) =>
                            (prev || []).map((x) => (String(x.serviceProductId) === String(r.serviceProductId) ? { ...x, value: v } : x))
                          );
                        })}
                      </Form.Item>

                      {activeInstallmentPlanMeta ? (
                        <ResponsiveFormGrid>
                          <Form.Item
                            name="paymentPlanInstallmentCount"
                            label={text("Installment payment plan").replace("{0}", activeInstallmentPlanMeta.planName)}
                            extra={text("Min max installments").replace("{0}", String(activeInstallmentPlanMeta.min)).replace("{1}", String(activeInstallmentPlanMeta.max))}
                            rules={[
                              {
                                validator: async (_, value) => {
                                  const n = value == null ? null : Number(value);
                                  if (!n || Number.isNaN(n)) throw new Error(text("Please select number of installments."));
                                  if (n < activeInstallmentPlanMeta.min || n > activeInstallmentPlanMeta.max) {
                                    throw new Error(text("Number of installments must be between min max").replace("{0}", String(activeInstallmentPlanMeta.min)).replace("{1}", String(activeInstallmentPlanMeta.max)));
                                  }
                                }
                              }
                            ]}
                          >
                            <InputNumber
                              min={activeInstallmentPlanMeta.min}
                              max={activeInstallmentPlanMeta.max}
                              step={1}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                          {details([
                            { key: "cycle", label: text("Payment cycle"), value: installmentPreview?.cycle || "—" },
                            {
                              key: "price",
                              label: installmentPreview?.count && installmentPreview.count > 1 ? text("Price per installment") : text("Price"),
                              value: installmentPreview
                                ? formatMoney(installmentPreview.count > 1 ? installmentPreview.installment : installmentPreview.total, units)
                                : "—"
                            },
                            { key: "total", label: text("Total"), value: installmentPreview ? formatMoney(installmentPreview.total, units) : "—" }
                          ])}
                        </ResponsiveFormGrid>
                      ) : null}

                      {paymentMethodField}
                      {details(visibleDetails)}
                    </RepeatableBlock>
                  );
                })
              )}
            </Flex>
          </>
        )}
      </FormSectionHeading>

      {selectedVariant && (selectedVariant.optionGroups || []).length ? (
        <>
          <FormSectionHeading title={text("Add-on options")} sectionKey="person-contract-create-contents.add-on-options">
            <ContractOptionGroupSelector
              usages={selectedVariant?.optionGroups || []}
              groups={contractOptionGroups}
              value={optionGroupSelection}
              onChange={setOptionGroupSelection}
              defaultPriceOf={contractTemplateDefaultPriceOf}
              units={units}
              text={text}
              readOnly={selectedVariantMeta?.allowOverridesOnCreate === false}
            />
          </FormSectionHeading>
        </>
      ) : null}

      {selectedVariant && (bundleWalletDraft || []).length ? (
        <>
          <FormSectionHeading title={text("Wallets")} sectionKey="person-contract-create-contents.wallets">
            <Flex vertical gap={spacing.md}>
              {(bundleWalletDraft || []).map((r, idx) => {
                const walletType = getWalletType({ ...r, walletProductType: r.walletProductType ?? undefined }, walletProducts);
                const walletDetails: ContractBlockDetail[] = [
                  { key: "balance", label: text("Initial balance"), value: formatWalletBalance(Number(r.initialBalance ?? 0), walletType, units) }
                ];
                if (r.validValue != null && r.validUnit != null) {
                  const u = timeUnitLabel(r.validUnit);
                  if (u) walletDetails.push({ key: "validity", label: text("Validity"), value: `${Number(r.validValue)} ${u}` });
                }
                if (r.usageLimitValue != null && r.usageLimitUnit != null) {
                  const u = usageLimitUnitLabel(r.usageLimitUnit);
                  if (u) walletDetails.push({ key: "usageLimit", label: text("Usage limit"), value: `${Number(r.usageLimitValue)} ${u}` });
                }
                const allowsAccess = r.allowsAccess !== false;
                const hasAccessScope = Number(r.accessScope ?? 0) > 0;
                if (allowsAccess && hasAccessScope) {
                  const access = accessScopeLabel(r.accessScope);
                  if (access) walletDetails.push({ key: BLOCK_DETAIL_ACCESS, label: text("Access"), value: access });
                  if (Number(r.accessScope ?? 0) === ACCESS_SCOPE_STUDIO_WHITELIST && (r.allowedStudioIds || []).length) {
                    const names = (r.allowedStudioIds || []).map((id) => allowedStudios.find((s) => String(s.id) === String(id))?.name || String(id));
                    walletDetails.push({ key: "allowedStudios", label: text("Allowed studios"), value: names.join(", ") });
                  }
                }
                if (r.requiresNoArrears) walletDetails.push({ key: "arrears", label: text("Arrears"), value: text("not allowed") });

                return (
                  <RepeatableBlock key={`${r.walletProductId}-${idx}`} title={r.walletProductName || text("Wallet")} removeLabel={text("Remove")}>
                    {details(walletDetails)}
                  </RepeatableBlock>
                );
              })}
            </Flex>
          </FormSectionHeading>
        </>
      ) : null}
    </>
  );
}
